import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '../lib/supabase';

export interface WeekStatus {
  week_number:    number;
  unlocked:       boolean;
  status:         'pending' | 'processing' | 'completed' | 'error';
  generated_at:   string | null;
  last_viewed_at: string | null;
  view_count:     number;
}

export interface AstroCoachStatus {
  subscription:      object | null;
  is_admin:          boolean;
  weeks_unlocked:    number;
  next_unlock_date:  string | null;
  weeks:             WeekStatus[];
  summary: {
    completed:   number;
    in_progress: number;
    pending:     number;
    locked:      number;
  };
}

export interface WeekSection {
  id:      string;
  title:   string;
  content: string;
}

export interface WeekContent {
  week_number:          number;
  planet:               string;
  title:                string;
  generated_at_transit: string;
  sections:             WeekSection[];
  reflections:          string[];
  weekly_practice:      { daily: string; weekly: string };
  mantra:               string;
  personalization_notes?: string;
}

export function useAstroCoach() {
  const { user } = useAuth();
  const [status,  setStatus]  = useState<AstroCoachStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!user) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await supabase.functions.invoke('astro-coach-status', {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (res.error) throw res.error;
      setStatus(res.data as AstroCoachStatus);
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar progresso');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const getWeekContent = useCallback(async (weekNumber: number): Promise<{
    source:       'cache' | 'generated' | 'processing';
    content:      WeekContent;
    generated_at: string;
  }> => {
    if (!user) throw new Error('Não autenticado');
    const res = await supabase.functions.invoke('astro-coach-week', {
      body: { userId: user.id, weekNumber },
    });
    if (res.error) {
      // Try to extract the actual error message from the response body
      try {
        const ctx = (res.error as unknown as { context?: Response }).context;
        if (ctx) {
          const body = await ctx.json();
          if (body?.error) throw new Error(body.error);
        }
      } catch (inner) {
        if (inner instanceof Error && inner.message !== res.error.message) throw inner;
      }
      throw res.error;
    }
    if (res.data?.error) throw new Error(res.data.error);
    await fetchStatus();
    return res.data;
  }, [user, fetchStatus]);

  return { status, loading, error, getWeekContent, refetch: fetchStatus };
}
